import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface AppNotification {
  id: string;
  title: string;
  body: string;
  icon?: string;
  createdAt: string;
  read: boolean;
  source: 'api' | 'fcm'; // track where it came from
}

interface NotificationState {
  items: AppNotification[];
  fcmIds: string[]; // track FCM pushed IDs to avoid duplicates
}

const initialState: NotificationState = {
  items: [],
  fcmIds: [],
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addFcmNotification(state, action: PayloadAction<AppNotification>) {
      // avoid duplicate if API already loaded this id
      const alreadyExists = state.items.some((n) => n.id === action.payload.id);
      if (alreadyExists) return;
      state.fcmIds.push(action.payload.id);
      state.items.unshift(action.payload);
    },
    setApiNotifications(state, action: PayloadAction<AppNotification[]>) {
      // merge API items, skip any already pushed via FCM
      const incoming = action.payload.filter((n) => !state.fcmIds.includes(n.id));
      // merge: FCM items on top, then API items (deduped by id)
      const existingIds = new Set(state.items.map((n) => n.id));
      for (const n of incoming) {
        if (!existingIds.has(n.id)) {
          state.items.push(n);
          existingIds.add(n.id);
        }
      }
    },
    appendApiNotifications(state, action: PayloadAction<AppNotification[]>) {
      // for load more — append older items
      const existingIds = new Set(state.items.map((n) => n.id));
      for (const n of action.payload) {
        if (!existingIds.has(n.id)) {
          state.items.push(n);
        }
      }
    },
    markItemsRead(state, action: PayloadAction<string[]>) {
      action.payload.forEach((id) => {
        const n = state.items.find((i) => i.id === id);
        if (n) n.read = true;
      });
    },
    markAllRead(state) {
      state.items.forEach((n) => (n.read = true));
    },
    removeItems(state, action: PayloadAction<string[]>) {
      const ids = new Set(action.payload);
      state.items = state.items.filter((n) => !ids.has(n.id));
    },
    clearAll(state) {
      state.items = [];
      state.fcmIds = [];
    },
  },
});

export const {
  addFcmNotification,
  setApiNotifications,
  appendApiNotifications,
  markItemsRead,
  markAllRead,
  removeItems,
  clearAll,
} = notificationSlice.actions;

export default notificationSlice.reducer;
